import { useState, useEffect } from 'react'
import {
  Bot,
  Power,
  PowerOff,
  Clock,
  CheckCircle,
  Server,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getRemoteAgentById } from '@/services/api'
import { RemoteAgent } from '@/types'

interface AgentDetailsDialogProps {
  agentId: string
}

const DetailItem = ({
  label,
  value,
  icon,
}: {
  label: string
  value: React.ReactNode
  icon: React.ReactNode
}) => (
  <div className="flex items-start gap-3">
    <div className="text-muted-foreground mt-1">{icon}</div>
    <div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="text-base font-semibold">{value}</div>
    </div>
  </div>
)

export const AgentDetailsDialog = ({ agentId }: AgentDetailsDialogProps) => {
  const [open, setOpen] = useState(false)
  const [agent, setAgent] = useState<RemoteAgent | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAgentDetails = async () => {
      if (!open || !agentId) return
      setIsLoading(true)
      setError(null)
      try {
        const agentDetails = await getRemoteAgentById(agentId)
        setAgent(agentDetails)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch agent details.',
        )
      } finally {
        setIsLoading(false)
      }
    }

    fetchAgentDetails()
  }, [open, agentId])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-4 py-4">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <div className="grid grid-cols-2 gap-4 pt-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <Alert variant="destructive" className="my-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )
    }

    if (!agent) {
      return null
    }

    return (
      <div className="space-y-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <DetailItem
            icon={<Power className="h-5 w-5" />}
            label="Status"
            value={
              <Badge
                variant={agent.status === 'online' ? 'default' : 'outline'}
                className={
                  agent.status === 'online'
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : ''
                }
              >
                {agent.status === 'online' ? (
                  <Power className="mr-2 h-4 w-4" />
                ) : (
                  <PowerOff className="mr-2 h-4 w-4" />
                )}
                {agent.status}
              </Badge>
            }
          />
          <DetailItem
            icon={<Clock className="h-5 w-5" />}
            label="Last Seen"
            value={formatDistanceToNow(new Date(agent.lastSeen), {
              addSuffix: true,
            })}
          />
          <DetailItem
            icon={<ExternalLink className="h-5 w-5" />}
            label="Endpoint URL"
            value={
              <span className="break-all text-sm">{agent.endpointUrl}</span>
            }
          />
          <DetailItem
            icon={<CheckCircle className="h-5 w-5" />}
            label="Tasks Completed"
            value={agent.tasksCompleted}
          />
          <DetailItem
            icon={<Server className="h-5 w-5" />}
            label="Uptime (seconds)"
            value={agent.uptime}
          />
        </div>
        <div>
          <h4 className="font-semibold mb-2">Configuration</h4>
          <pre className="bg-code-block p-3 rounded-md text-sm overflow-x-auto">
            <code>{JSON.stringify(agent.configuration, null, 2)}</code>
          </pre>
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Details
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            {agent?.name || 'Agent Details'}
          </DialogTitle>
          <DialogDescription>{agent?.description}</DialogDescription>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  )
}
